import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { getYear, formatDateDE } from "../../utils/affiliateHelper";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 1999 }, (_, i) => CURRENT_YEAR - i);

export default function ReleasesPage() {
  const [releases, setReleases] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const fetchReleases = useCallback(async (p = 1, q = "", y = "") => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (q) params.search = q;
      if (y) params.year = y;
      const res = await api.get("/releases/", { params });
      setReleases(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(p);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReleases(1, "", ""); }, [fetchReleases]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReleases(1, search, year);
  };

  const handleYearChange = (e) => {
    const y = e.target.value;
    setYear(y);
    setSearch("");
    fetchReleases(1, "", y);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Release wirklich löschen?")) return;
    try {
      await api.delete(`/releases/${id}`);
      fetchReleases(page, search, year);
    } catch {
      alert("Fehler beim Löschen");
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-white font-heading">Releases</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{total.toLocaleString("de-DE")} Einträge</p>
        </div>
        <Link to="/admin/releases/new" data-testid="add-release-btn"
          className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-md transition-colors">
          <Plus size={16} /> Neuer Release
        </Link>
      </div>

      {/* Search + Year filter */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Interpret oder Titel suchen..."
            className="flex-1 bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600"
            data-testid="admin-release-search"
          />
          <button type="submit" className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md transition-colors">
            <Search size={16} />
          </button>
        </form>
        <select
          value={year} onChange={handleYearChange}
          className="bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-rose-600 transition-colors"
          data-testid="admin-release-year-filter"
        >
          <option value="">Alle Jahre</option>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full admin-table" data-testid="releases-table">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-4 py-3 text-left">Cover</th>
                <th className="px-4 py-3 text-left">Interpret / Titel</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Label</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Datum</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Typ</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-4 py-3">
                      <div className="h-4 skeleton rounded w-full" />
                    </td>
                  </tr>
                ))
              ) : releases.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-800/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="w-10 h-10 rounded overflow-hidden bg-zinc-800 flex-shrink-0">
                      {r.cover_url
                        ? <img src={r.cover_url} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display="none"} />
                        : <div className="w-full h-full cover-fallback text-xs">{r.interpret?.[0]}{r.titel?.[0]}</div>
                      }
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-white text-sm font-semibold truncate max-w-xs">{r.interpret}</p>
                    <p className="text-zinc-500 text-xs truncate max-w-xs">{r.titel}</p>
                    {(r.created_by_name || r.updated_by_name) && (
                      <p className="text-zinc-500 text-xs mt-0.5 flex items-center gap-1">
                        <span>von</span>
                        <span className="text-rose-400/70">{r.created_by_name || r.updated_by_name}</span>
                        {r.updated_by_name && r.updated_by_name !== r.created_by_name && r.created_by_name && (
                          <span className="text-zinc-600">· bearb. {r.updated_by_name}</span>
                        )}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-zinc-400 text-sm">{r.label || "–"}</span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-zinc-400 text-sm">{formatDateDE(r.erscheinungsdatum)}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {r.typ && <span className="bg-zinc-800 text-zinc-300 text-xs px-2 py-0.5 rounded-full">{r.typ}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/releases/${r.id}/edit`} data-testid="edit-release-btn"
                        className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors">
                        <Edit size={15} />
                      </Link>
                      <button onClick={() => handleDelete(r.id)} data-testid="delete-release-btn"
                        className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <span className="text-zinc-500 text-xs">Seite {page} von {pages}</span>
            <div className="flex gap-1">
              <button onClick={() => fetchReleases(page - 1, search, year)} disabled={page === 1}
                className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-30 rounded transition-colors">
                <ChevronLeft size={16} />
              </button>
              <button onClick={() => fetchReleases(page + 1, search, year)} disabled={page === pages}
                className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-30 rounded transition-colors">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}
